import { contentScores } from './content-based';
import { itemBasedScores, userBasedScores } from './collaborative';
import { knowledgeScores } from './knowledge-based';
import { temporalScores } from './temporal';
import { daysToTarget, forecastEndOfYear, schoolMonthIndex } from './predictive';
import { buildExplanations } from './explanations';

export const DEFAULT_WEIGHTS = {
  content: 0.15,
  userCF: 0.30,
  itemCF: 0.15,
  knowledge: 0.25,
  temporal: 0.10,
  predictive: 0.05,
};

const estimateActivityCredits = (activity) =>
  activity.credite_acordate ||
  Math.max(1, Math.ceil((Number(activity.credite_per_ora) || 1) * (activity.ore_recomandate || 1)));

const round = (value) => Math.round(value * 1000) / 1000;

const normalizeName = (name) =>
  String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export const selectDiverse = (scored, k = 5, maxPerModule = 2) => {
  const counters = new Map();
  const selected = [];
  const used = new Set();

  const place = (item, relax) => {
    const options = item.modules && item.modules.length ? item.modules : [item.module];
    const available = options.filter((module) => (counters.get(module) || 0) < maxPerModule);
    let chosen = [...available].sort(
      (a, b) => (counters.get(a) || 0) - (counters.get(b) || 0),
    )[0];
    if (!chosen && relax) {
      chosen = [...options].sort((a, b) => (counters.get(a) || 0) - (counters.get(b) || 0))[0];
    }
    if (!chosen) return false;
    counters.set(chosen, (counters.get(chosen) || 0) + 1);

    const placed = { ...item, module: chosen, originalModule: item.module };
    const moduleKnowledge = item.knowledgeByModule?.[chosen];
    if (moduleKnowledge) {
      placed.signals = {
        ...placed.signals,
        knowledge: { ...placed.signals.knowledge, ...moduleKnowledge, moduleCode: chosen },
        temporal: { ...placed.signals.temporal, moduleCode: chosen },
      };
      placed.explanation = buildExplanations(placed);
    }

    selected.push(placed);
    used.add(item);
    return true;
  };

  let progress = true;
  while (selected.length < k && progress) {
    progress = false;
    for (const item of scored) {
      if (selected.length >= k) break;
      if (used.has(item)) continue;
      if (place(item, false)) progress = true;
    }
  }

  for (const item of scored) {
    if (selected.length >= k) break;
    if (used.has(item)) continue;
    place(item, true);
  }

  return selected;
};

export const scoreCandidates = (dataset, profiles, studentId, options = {}) => {
  const profile = profiles.get(studentId);
  if (!profile) return [];

  const weights = { ...DEFAULT_WEIGHTS, ...(options.weights || {}) };
  const date = options.date || new Date();

  const content = contentScores(dataset, profile);
  const userCF = userBasedScores(dataset, profiles, studentId, options.neighborCount || 10);
  const itemCF = itemBasedScores(dataset, profiles, studentId);
  const knowledge = knowledgeScores(dataset, profiles, studentId);
  const temporal = temporalScores(dataset, profile, date);

  const monthIndex = Math.min(9, Math.max(0, schoolMonthIndex(date.toISOString())));
  const forecast = forecastEndOfYear(profile.monthlyCumulative, dataset.targetSum, monthIndex);
  const needed = Math.max(0, dataset.targetSum - profile.totalCredits);
  const dailyRate = profile.growthSlope > 0 ? profile.growthSlope / 30 : 0;

  const doneKeys = new Set(profile.activityKeys || []);
  const doneNames = new Set(profile.activityNames);
  const lookupSignal = (map, activity) =>
    map.get(`id:${activity.id}`) ||
    map.get(`name:${normalizeName(activity.nume_activitate)}`) ||
    null;

  const buildRecommendation = (activity) => {
    const estimatedCredits = estimateActivityCredits(activity);
    const module = dataset.moduleById.get(activity.id_module);
    const type = dataset.typeById.get(activity.id_tip_credit);

    const contentSignal = content.get(activity.id);
    const userSignal = lookupSignal(userCF, activity);
    const itemSignal = lookupSignal(itemCF, activity);
    const knowledgeSignal = knowledge.get(activity.id);
    const temporalSignal = temporal.get(activity.id);

    const predictiveValue = needed > 0 ? Math.min(1, estimatedCredits / Math.max(1, needed)) : 0.3;
    let daysSaved = 0;
    if (dailyRate > 0 && needed > 0) {
      const withoutActivity = daysToTarget(profile.totalCredits, dataset.targetSum, dailyRate) || 0;
      const withActivity = daysToTarget(profile.totalCredits + estimatedCredits, dataset.targetSum, dailyRate) || 0;
      daysSaved = Math.max(0, withoutActivity - withActivity);
    }

    const breakdown = {
      content: contentSignal?.score || 0,
      userCF: userSignal?.score || 0,
      itemCF: itemSignal?.score || 0,
      knowledge: knowledgeSignal?.score || 0,
      temporal: temporalSignal?.score || 0,
      predictive: predictiveValue,
    };

    const finalScore =
      weights.content * breakdown.content +
      weights.userCF * breakdown.userCF +
      weights.itemCF * breakdown.itemCF +
      weights.knowledge * breakdown.knowledge +
      weights.temporal * breakdown.temporal +
      weights.predictive * breakdown.predictive;

    const contributions = Object.fromEntries(
      Object.entries(breakdown).map(([key, value]) => [key, round(value * (weights[key] || 0))]),
    );

    const recommendation = {
      activityId: activity.id,
      activityName: activity.nume_activitate,
      module: module?.cod_module || '-',
      moduleName: module?.nume_module || '',
      category: type?.categorie || 'curricular',
      estimatedCredits,
      finalScore: round(finalScore),
      breakdown: Object.fromEntries(Object.entries(breakdown).map(([key, value]) => [key, round(value)])),
      contributions,
      signals: {
        content: { anchorName: contentSignal?.anchorName },
        userCF: { neighborCount: userSignal?.neighborCount || 0 },
        itemCF: { neighborCount: itemSignal?.neighborCount || 0 },
        knowledge: {
          deficit: knowledgeSignal?.deficit || 0,
          moduleCode: knowledgeSignal?.moduleCode || module?.cod_module,
          rolloverCredits: knowledgeSignal?.rolloverCredits || 0,
          rule: knowledgeSignal?.rule,
          enrichment: knowledgeSignal?.enrichment || false,
          totalCredits: knowledgeSignal?.totalCredits || profile.totalCredits,
          targetSum: knowledgeSignal?.targetSum || dataset.targetSum,
        },
        temporal: { available: temporalSignal?.available || false, moduleCode: module?.cod_module },
        predictive: { daysSaved, needed },
      },
    };

    return {
      ...recommendation,
      forecast,
      explanation: buildExplanations(recommendation),
    };
  };

  const groups = new Map();
  dataset.activities
    .filter(
      (activity) => !doneKeys.has(`id:${activity.id}`) && !doneNames.has(activity.nume_activitate),
    )
    .forEach((activity) => {
      const key = normalizeName(activity.nume_activitate);
      const group = groups.get(key) || { name: activity.nume_activitate, variants: [] };
      group.variants.push(activity);
      groups.set(key, group);
    });

  return [...groups.values()]
    .map((group) => {
      const scoredVariants = group.variants
        .map(buildRecommendation)
        .sort((a, b) => b.finalScore - a.finalScore);
      const best = scoredVariants[0];
      const modules = [...new Set(scoredVariants.map((variant) => variant.module))];
      const knowledgeByModule = {};
      scoredVariants.forEach((variant) => {
        knowledgeByModule[variant.module] = variant.signals.knowledge;
      });
      return {
        ...best,
        activityName: group.name,
        modules,
        moduleLabel: modules.join(', '),
        knowledgeByModule,
      };
    })
    .sort(
      (a, b) =>
        b.finalScore - a.finalScore ||
        (b.modules?.length || 1) - (a.modules?.length || 1),
    );
};

export const recommend = (dataset, profiles, studentId, options = {}) =>
  selectDiverse(
    scoreCandidates(dataset, profiles, studentId, options),
    options.k || 5,
    options.maxPerModule ?? 2,
  );
